import type React from "react";
import type { ReactNode } from "react";

import { useImageResolver } from "@/api/hooks/useImageResolver";
import type { ImageDto } from "@/api/types";
import bgPlaceholder from "@/assets/images/image-placeholder.png";
import { Carousel } from "@/components/Carousel/Carousel";
import { ContextMenu, type ContextMenuItem } from "@/components/ContextMenu/ContextMenu";
import { ShareButton } from "@/components/ShareButton/ShareButton";
import { Card } from "@/design-system/Card";
import { Inline } from "@/design-system/Inline";
import { Stack } from "@/design-system/Stack";
import type { SupportedDeepLinkEntityType } from "@/helpers/deep-link";
import { useTranslation } from "@/translations";
import { RuntimeBackgroundImage } from "@/ui-adapters/RuntimeBackgroundImage";

import { MultiLineContent } from "../MultiLineContent/MultiLineContent";

type EntityDetailsCardProps = {
  title: string;
  description: string;
  images?: ImageDto[];
  actions?: ContextMenuItem[];
  children?: ReactNode;
  infoItems?: {
    icon: ReactNode;
    label: string;
    value: ReactNode;
  }[];
  renderBadges?: () => ReactNode;
  renderBody?: () => ReactNode;
  renderCtaButton?: () => ReactNode;
  renderFooter?: () => ReactNode;
} & (
  | {
      allowShare: boolean;
      entityId: string;
      entityType: SupportedDeepLinkEntityType;
      otherIds?: Record<string, string>;
    }
  | {
      allowShare?: false;
      entityId?: never;
      entityType?: never;
      otherIds?: never;
    }
);

export function EntityDetailsCard({
  title,
  description,
  images,
  actions,
  allowShare,
  entityType,
  infoItems,
  entityId,
  otherIds,
  renderBadges,
  renderBody,
  renderCtaButton,
  renderFooter,
}: EntityDetailsCardProps): React.ReactNode {
  const resolveImage = useImageResolver();
  const { t } = useTranslation();

  const isCarousel = images && images.length > 1;

  return (
    <Card as="article" density="flush" clip>
      <header className="relative h-entity-detail-media w-full overflow-hidden rounded-t-xl bg-background-tertiary bg-cover bg-center @md:h-entity-detail-media-md @lg:h-entity-detail-media-lg">
        {!isCarousel && (
          <RuntimeBackgroundImage
            imageUrl={images?.length === 1 ? resolveImage(images[0], "intrinsic") : bgPlaceholder}
            presentation="transparentFill"
          />
        )}
        {renderBadges && <aside className="absolute top-3 left-3 flex gap-space-sm">{renderBadges()}</aside>}
        {isCarousel && <Carousel allowZoom {...{ images }} />}
      </header>
      <section className="flex flex-col gap-space-md p-space-md">
        <Inline gap="md" align="center" justify="between">
          <h2 className="w-full text-heading-3 leading-none wrap-break-word @sm:w-auto">{title}</h2>
          <div className="flex w-full flex-wrap items-center justify-end gap-space-sm @sm:w-auto">
            {renderCtaButton?.()}
            {allowShare && <ShareButton entityId={entityId} entityType={entityType} otherIds={otherIds} />}
            {actions && <ContextMenu items={actions} />}
          </div>
        </Inline>
        {renderBody && renderBody()}
        <Stack gap="xs">
          <span className="text-body-emphasis">{t("model.document.root.description")}</span>
          <MultiLineContent shouldHighlightLinks>{description.trim()}</MultiLineContent>
        </Stack>

        {infoItems && infoItems.length > 0 && (
          <Stack as="ul" gap="xs" align="start">
            {infoItems.map((item, index) => {
              if (!item.value) return;

              return (
                <li key={index}>
                  <Inline gap="xs">
                    <span className="flex items-center gap-space-xs text-text-secondary">
                      <span>{item.icon}</span>
                      <span>{item.label}:</span>
                    </span>
                    <span>{item.value}</span>
                  </Inline>
                </li>
              );
            })}
          </Stack>
        )}

        {renderFooter && renderFooter()}
      </section>
    </Card>
  );
}
